import classNames from 'classnames';

import { StatusDotOnColor, StatusDotVariant } from './constants';
import { IconSize } from '../..';
import { AnalyticsId } from '../../constants';
import { getColor } from '../../theme/currys';
import { LazyIcon } from '../LazyIcon';

import styles from './styles.module.scss';

export interface StatusDotIconProps {
  /** Defines the color of the icon */
  onColor?: keyof typeof StatusDotOnColor;
  /** The variant defines style formatting and what icon to use */
  variant?: keyof typeof StatusDotVariant;
}

const StatusDotIcon: React.FC<StatusDotIconProps> = ({ onColor, variant = 'info' }) => {
  const color = onColor === StatusDotOnColor.ondark ? getColor('white') : getColor('black');
  const iconProps = {
    color,
    size: IconSize.XXSmall,
    onColor,
    className: classNames({
      [styles[`statusdot__dot--${variant}`]]: typeof variant !== 'undefined',
      [styles['statusdot__dot--on-dark']]: onColor === StatusDotOnColor.ondark,
    }),
  };

  switch (variant) {
    case StatusDotVariant.success:
      return <LazyIcon {...iconProps} iconName={'DotCheckmark'} />;
    case StatusDotVariant.inprocess:
      return <LazyIcon {...iconProps} iconName={'DotHalfDisc'} />;
    case StatusDotVariant.exception:
      return <LazyIcon {...iconProps} iconName={'DotTriangle'} />;
    case StatusDotVariant.unknown:
      return <LazyIcon {...iconProps} iconName={'DotQuestionMark'} />;
    case StatusDotVariant.inspected:
      return <LazyIcon {...iconProps} iconName={'DotLookingGlass'} />;
    case StatusDotVariant.cancelled:
      return <LazyIcon {...iconProps} iconName={'DotX'} />;
    case StatusDotVariant.alert:
      return <LazyIcon {...iconProps} iconName={'DotExclamationMark'} />;
    case StatusDotVariant.transparent:
      return <LazyIcon {...iconProps} iconName={'DotOutline'} />;
    case StatusDotVariant.info:
      return <LazyIcon {...iconProps} iconName={'DotInfo'} />;
    case StatusDotVariant.group:
      return <LazyIcon {...iconProps} iconName={'DotGroup'} />;
    case StatusDotVariant.recurring:
      return <LazyIcon {...iconProps} iconName={'DotCircleArrows'} />;
    case StatusDotVariant.noaccess:
      return <LazyIcon {...iconProps} iconName={'DotNoAccess'} />;
    case StatusDotVariant.draft:
      return <LazyIcon {...iconProps} iconName={'DotPencil'} />;
    case StatusDotVariant.hidden:
      return <LazyIcon {...iconProps} iconName={'DotNoEye'} />;
    case StatusDotVariant.login:
      return <LazyIcon {...iconProps} iconName={'DotLogin'} />;
    case StatusDotVariant.attachment:
      return <LazyIcon {...iconProps} iconName={'DotAttachment'} />;
    case StatusDotVariant.active:
      return <LazyIcon {...iconProps} iconName={'DotFill'} />;
    case StatusDotVariant.pending:
      return <LazyIcon {...iconProps} iconName={'DotDot'} />;
    case StatusDotVariant.inactive:
      return <LazyIcon {...iconProps} iconName={'DotOutline'} />;
    case StatusDotVariant.ready:
      return <LazyIcon {...iconProps} iconName={'DotOutline'} />;
  }

  return null;
};

export interface StatusDotProps {
  /** id that is placed on the wrapper */
  id?: string;
  /** Defines the color mode, onwhite, ondark etc. */
  onColor?: keyof typeof StatusDotOnColor;
  /** Visual variants for the statusdot */
  variant?: keyof typeof StatusDotVariant;
  /** Text placed to the right of the statusdot */
  text: string;
  /** Adds custom classes to the element. */
  className?: string;
  /** Sets the data-testid attribute. */
  testId?: string;
  /** Ref passed to the root element */
  ref?: React.Ref<HTMLElement | null>;
}

export type StatusDotType = typeof StatusDot;
const StatusDot: React.FC<StatusDotProps> = (props: StatusDotProps) => {
  const { id, onColor = StatusDotOnColor.onwhite, variant = StatusDotVariant.info, text, className, testId, ref } = props;
  const statusDotClasses = classNames(styles['statusdot'], className);
  const labelClasses = classNames(styles['statusdot__label'], {
    [styles['statusdot__label--on-dark']]: onColor === StatusDotOnColor.ondark,
  });

  return (
    <span id={id} ref={ref} className={statusDotClasses} data-testid={testId} data-analyticsid={AnalyticsId.StatusDot}>
      <span className={styles['statusdot__dot']} data-testid={testId + '-dot'}>
        <StatusDotIcon onColor={onColor} variant={variant} />
      </span>
      <span className={labelClasses}>{text}</span>
    </span>
  );
};

export default StatusDot;
